import './App.css';
import { useState, useRef, useLayoutEffect, useEffect, useMemo   } from 'react';
import { DesktopHeader, MobileHeader, MobileFooter, Box } from 'src/components';
import { useIsMobile, useSafeAreaInsets, useClarity } from 'src/hooks';
import { useAuth, getChatsForCurrentUser, SignalRProvider, isOnline } from 'src/utils';
import { navItems, mapUserChat } from 'src/data';
import { useMapStore } from 'src/stores';
import { useNavigate } from 'react-router-dom';
import { SplashScreen } from '@capacitor/splash-screen';
import AppRoutes from 'src/app-routing';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useMessagingStore } from "src/stores";
import SignalRListener from './signal-r-listener';

const App = () => {

  const footerRef = useRef();
  const [footerHeight, setFooterHeight] = useState(55);
  const [handbookMenuTab, setHandbookMenuTab] = useState(null); // null = closed

  const isMobile = useIsMobile();
  const { user, isAuthReady, isFetching } = useAuth();
  const navigate = useNavigate();

  useSafeAreaInsets();
  useClarity(user?.id);

  const setAllLastRead = useMessagingStore((s) => s.setAllLastRead);
  const lastReadTimestamps = useMessagingStore((s) => s.lastReadTimestamps);
  const [allUserChats, setAllUserChats] = useState([]);
  const [chatUpdates, setChatUpdates] = useState({});
  const [loadingUserChats, setLoadingUserChats] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const googleConfig = {
      webClientId: '957493528677-4pnl786brbka6fq5ai4901uqka4ku40e.apps.googleusercontent.com',
      iOSClientId: '957493528677-cglfgjnef01e24eqa2dernj860ifvk9u.apps.googleusercontent.com',
    };

    const config = {
      google: googleConfig,
      ...(Capacitor.getPlatform() === 'ios' && {
        apple: {
          clientId: 'com.panamhighway.panamexplorer',
          useBroadcastChannel: true,
          useProperTokenExchange: false,
        },
      }),
    };

    SocialLogin.initialize(config);
  }, []);

  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (isAuthReady) {
        SplashScreen.hide({ fadeOutDuration: 300 });
      }
    }, 8000);

    return () => clearTimeout(safetyTimeout);
  }, [isAuthReady]);

  useEffect(() => {
    if (!user) {
      setChatUpdates({});
      setAllUserChats([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user && !isFetching && isAuthReady && isMobile && isOnline()) {
      navigate('/');
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;

    async function loadChats() {
      setLoadingUserChats(true);
      try {
        const res = await getChatsForCurrentUser();
        const mapped = res.map((chat) => mapUserChat(chat, user.id, chat.type));
        setAllUserChats(mapped);

        const lastReadMap = {};
        mapped.forEach((c) => {
          if (c.currentUserLastReadAt) lastReadMap[c.id] = c.currentUserLastReadAt;
        });
        setAllLastRead(lastReadMap);
      } catch (err) {
        console.error("Error fetching users chats", err);
      } finally {
        setLoadingUserChats(false);
      }
    }

    loadChats();
  }, [user?.id, setAllLastRead]);

  useLayoutEffect(() => {
    const updateFooterHeight = () => {
      if (footerRef.current) {
        setFooterHeight(footerRef.current.getBoundingClientRect().height);
      }
  };

  updateFooterHeight();
      const resizeObserver = new ResizeObserver(updateFooterHeight);
      if (footerRef.current) resizeObserver.observe(footerRef.current);

      window.addEventListener('resize', updateFooterHeight);

      return () => {
        window.removeEventListener('resize', updateFooterHeight);
        if (footerRef.current) resizeObserver.disconnect();
      };
  }, []);

  useEffect(() => {
    const { cleanup } = useMapStore.getState();
    
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const backHandler = CapacitorApp.addListener('backButton', () => {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
    });

    return () => {
      backHandler.remove();
    };
  }, [navigate]);
  
  const hasUnreadChats = useMemo(() => {
    const initialLoad = allUserChats.some(chat =>
      chat.lastMessageSentBy !== user?.id &&
      chat.lastMessageSentAt &&
      (!lastReadTimestamps[chat.id] ||
      new Date(chat.lastMessageSentAt) > new Date(lastReadTimestamps[chat.id]))
    );
    const signalRLoad = Object.values(chatUpdates).some(update => {
      const currentUserMessage = update.senderId === user?.id;
      const lastRead = lastReadTimestamps[update.chatId];
      return !currentUserMessage && update.timestamp && (!lastRead || new Date(update.timestamp) > new Date(lastRead));
    });
    return initialLoad || signalRLoad;
  }, [allUserChats, lastReadTimestamps, chatUpdates, user?.id]);

  return (
    <SignalRProvider chats={allUserChats}>
      <div className="app-container">

        <SignalRListener setChatUpdates={setChatUpdates}/>

        {isMobile ? (
          <Box className="top-level-container-mobile">

            <Box 
              component="main" 
              className="main-container-mobile"
              sx={{
                paddingBottom: 'var(--safe-area-bottom)',
                paddingLeft: 'var(--safe-area-left)',
                paddingRight: 'var(--safe-area-right)'
              }}>

              <MobileHeader 
                handbookMenuTab={handbookMenuTab} 
                setHandbookMenuTab={setHandbookMenuTab}/>

              <div className="view-container-mobile">
                  <AppRoutes 
                      userChats={allUserChats}
                      chatUpdates={chatUpdates}
                      userHasUnreadChats={hasUnreadChats} 
                      loadingUserChats={loadingUserChats}
                      footerHeight={footerHeight} 
                      setHandbookMenuTab={setHandbookMenuTab}
                  />
              </div>

            </Box>

            {user && (user.hasCompletedOnboarding || !isOnline()) ? (
              <MobileFooter 
                navItems={navItems} 
                userHasUnreadChats={hasUnreadChats} 
                ref={footerRef}
              />
            ) : null}

          </Box>
        ) : (
          <Box className="top-level-container-desktop">

            <Box component="main" className="main-container-desktop">

                <DesktopHeader 
                  navItems={navItems} 
                  userHasUnreadChats={hasUnreadChats} 
                  handbookMenuTab={handbookMenuTab} 
                  setHandbookMenuTab={setHandbookMenuTab}/>

                <div className="view-container-desktop">
                    <AppRoutes 
                        userChats={allUserChats}
                        chatUpdates={chatUpdates}
                        loadingUserChats={loadingUserChats}
                        setHandbookMenuTab={setHandbookMenuTab}
                    />
                </div>

            </Box>

          </Box>
        )}
      </div>
    </SignalRProvider>
  );
};

export default App;



